/**
 * X-USER-BACK — pure core for the report header's "‹ Reports" action.
 *
 * DESTINATION (William 2026-07-03): the TOP-LEVEL Reports tab
 * (`/?tab=reports`), not the site's reports list — the button reads
 * "‹ REPORTS" and users expect the global reports surface.
 *
 * HISTORY (2026-07-02, company-shared Redhead report): the original handler
 * navigated and relied SOLELY on `useUrlSiteHydration` to clear
 * `activeReportId` (engineView is SiteRagEngine-DERIVED — a non-null
 * activeReport promotes to 'report'). `hydrateSiteragFromUrl` EARLY-RETURNS
 * when the report's site does not resolve locally (cross-user shape), so the
 * clear never ran and Back was a no-op. With the dashboard destination the
 * hydration bail is UNIVERSAL (`/` carries no siteId), so the explicit store
 * clears below are the ONLY demotion path — for every report, not just
 * cross-user ones.
 *
 * Order matters — react-router's `navigate()` pushes the new URL into
 * `window.history` synchronously, so when the store writes below re-run the
 * hydration effect, `hydrate()` reads the dashboard path and cannot re-open
 * the report (the historic 2-click bounce). Clearing BOTH ids matters:
 * `activeReportId` null alone would leave `activeSiteId` set and the derived
 * engineView lands on 'site' — the dashboard (which owns the `?tab=reports`
 * surface) only renders once the site clears too. The `loadReports`
 * auto-select half of the old race is fixed separately in the store
 * (fetchRagReports never auto-selects).
 */
export function navigateBackToReports(args: {
  navigate: (to: string) => void;
  setActiveSite: (id: string | null) => void;
  setActiveRagReport: (id: string | null) => void;
  /** Owning site — when known, land on ITS Findings tab (the decision hub
   *  that replaced the per-site reports list; William 2026-07-28) instead
   *  of the top-level dashboard. */
  siteId?: string | null;
}): void {
  const { navigate, setActiveSite, setActiveRagReport, siteId } = args;
  if (siteId) {
    // Keep the site active — only the open report clears. `?tab=reports`
    // renders the Findings dossier under the decision-findings rollout.
    navigate(`/sites/${siteId}?tab=reports`);
    setActiveRagReport(null);
    return;
  }
  navigate('/?tab=reports');
  setActiveSite(null);
  setActiveRagReport(null);
}
